import { create } from 'zustand'

import { aiService } from '@/modules/ai/services'
import type {
  AIConfig,
  AIHistoryEntry,
  AIInsightBundle,
  CommercialObservationsResult,
  QuoteAnalysisInput,
  QuoteAnalysisResult,
  TechnicalDescriptionResult,
} from '@/modules/ai/types'
import { DEFAULT_AI_CONFIG } from '@/modules/ai/types'

interface AIStoreState {
  ready: boolean
  config: AIConfig
  quoteInput: QuoteAnalysisInput | null
  analysis: QuoteAnalysisResult | null
  insights: AIInsightBundle | null
  description: TechnicalDescriptionResult | null
  observations: CommercialObservationsResult | null
  history: AIHistoryEntry[]
  disclaimer: string | null
  loading: boolean
  lastError: string | null

  hydrate: () => void
  setQuoteInput: (input: QuoteAnalysisInput) => void
  loadFromDraft: (items: Parameters<typeof aiService.draftToQuoteInput>[0]) => void
  loadSample: () => void
  saveConfig: (config: AIConfig) => void
  analyzeQuote: () => Promise<void>
  generateInsights: () => Promise<void>
  generateDescription: () => Promise<void>
  generateObservations: () => Promise<void>
}

export const useAIStore = create<AIStoreState>((set, get) => ({
  ready: false,
  config: DEFAULT_AI_CONFIG,
  quoteInput: null,
  analysis: null,
  insights: null,
  description: null,
  observations: null,
  history: [],
  disclaimer: null,
  loading: false,
  lastError: null,

  hydrate: () => {
    set({
      ready: true,
      config: aiService.getConfig(),
      history: aiService.listHistory(),
    })
  },

  setQuoteInput: (input) => set({ quoteInput: input }),

  loadFromDraft: (items) => {
    const input =
      items.length > 0 ? aiService.draftToQuoteInput(items) : aiService.sampleQuoteInput()
    set({ quoteInput: input, ready: true })
  },

  loadSample: () => set({ quoteInput: aiService.sampleQuoteInput() }),

  saveConfig: (config) => {
    const saved = aiService.saveConfig(config)
    set({ config: saved })
  },

  analyzeQuote: async () => {
    const input = get().quoteInput
    if (!input) {
      set({ lastError: 'Nenhum orçamento carregado para análise.' })
      return
    }
    set({ loading: true, lastError: null })
    try {
      const response = await aiService.analyzeQuote(input)
      const insights = await aiService.generateInsights(input)
      set({
        analysis: response.data,
        insights: insights.data,
        disclaimer: response.disclaimer,
        history: aiService.listHistory(),
        loading: false,
      })
    } catch (error) {
      set({
        loading: false,
        lastError: error instanceof Error ? error.message : 'Falha na análise',
      })
    }
  },

  generateInsights: async () => {
    const input = get().quoteInput
    if (!input) return
    set({ loading: true })
    const response = await aiService.generateInsights(input)
    set({
      insights: response.data,
      disclaimer: response.disclaimer,
      history: aiService.listHistory(),
      loading: false,
    })
  },

  generateDescription: async () => {
    const input = get().quoteInput
    if (!input) {
      set({ lastError: 'Carregue um orçamento antes de gerar descrição.' })
      return
    }
    set({ loading: true, lastError: null })
    const response = await aiService.generateDescription(input)
    set({
      description: response.data,
      disclaimer: response.disclaimer,
      history: aiService.listHistory(),
      loading: false,
    })
  },

  generateObservations: async () => {
    const input = get().quoteInput
    if (!input) {
      set({ lastError: 'Carregue um orçamento antes de gerar observações.' })
      return
    }
    set({ loading: true, lastError: null })
    const response = await aiService.generateObservations(input)
    set({
      observations: response.data,
      disclaimer: response.disclaimer,
      history: aiService.listHistory(),
      loading: false,
    })
  },
}))
